import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Breadcrumb, Card, Statistic, Row, Col, Select, message, Modal, Spin, Tag } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
    TrophyOutlined,
    ReloadOutlined,
    EyeOutlined,
    CalendarOutlined,
    TeamOutlined,
    DollarOutlined,
    RightOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { rankingsService } from '../../common/services';
import dayjs from 'dayjs';
import Can from '../../components/can';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
};

const formatPeriod = (p) => {
    if (!p) return '';
    const [year, month] = p.split('-');
    const months = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
};

const Rankings = () => {
    const [periods, setPeriods] = useState([]);
    const [stats, setStats] = useState(null);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [recalculating, setRecalculating] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const navigate = useNavigate();

    // Charger les villes disponibles
    const loadCities = useCallback(async () => {
        try {
            const response = await rankingsService.getCities();
            if (response.success) {
                const loadedCities = response.data.cities || [];
                setCities(loadedCities);
                // Auto-sélectionner la ville par défaut
                const defaultCity = loadedCities.find(c => c.isDefault);
                if (defaultCity && !selectedCity) {
                    setSelectedCity(defaultCity._id);
                }
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            // Params avec filtre ville
            const params = {};
            if (selectedCity) {
                params.city = selectedCity;
            }

            // Charger la liste des periodes
            const response = await rankingsService.getRankings(params);
            if (response.success) {
                setPeriods(response.data.periods || []);
            }

            // Charger les stats globales (avec filtre ville)
            const statsResponse = await rankingsService.getStats(params);
            if (statsResponse.success) {
                setStats(statsResponse.data);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des donnees');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [selectedCity]);

    useEffect(() => {
        loadCities();
    }, [loadCities]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRecalculate = async (fullScan = false) => {
        const modeLabel = fullScan ? 'scan complet' : 'rapide';
        const modeDesc = fullScan
            ? 'Tous les chauffeurs seront verifies (peut prendre plus de 2 heures).'
            : 'Seuls les chauffeurs deja classes seront mis a jour (environ 5 minutes).';

        Modal.confirm({
            title: `Recalculer le classement (${modeLabel})`,
            content: `Voulez-vous recalculer le classement pour ${selectedPeriod ? formatPeriod(selectedPeriod) : 'le mois en cours'} ? ${modeDesc}`,
            okText: 'Recalculer',
            cancelText: 'Annuler',
            onOk: async () => {
                setRecalculating(true);
                try {
                    const response = await rankingsService.recalculate(selectedPeriod, fullScan);
                    if (response.success) {
                        message.success(`Classement recalcule avec succes (mode ${modeLabel})`);
                        loadData();
                    } else {
                        message.error(response.message || 'Erreur lors du recalcul');
                    }
                } catch (error) {
                    message.error('Erreur lors du recalcul');
                    console.error(error);
                } finally {
                    setRecalculating(false);
                }
            }
        });
    };

    const columns = [
        {
            title: 'Periode',
            dataIndex: 'period',
            key: 'period',
            render: (period) => (
                <span style={{ fontWeight: 600 }}>
                    <CalendarOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                    {formatPeriod(period)}
                </span>
            )
        },
        {
            title: 'Chauffeurs',
            dataIndex: 'driversCount',
            key: 'driversCount',
            render: (count) => (
                <span>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    {count}
                </span>
            )
        },
        {
            title: 'Top 1',
            key: 'topDriver',
            render: (_, record) => (
                record.topDriver ? (
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.topDriver.firstName} {record.topDriver.lastName}</div>
                        <div style={{ fontSize: 12, color: '#999' }}>{record.topDriver.phone}</div>
                        <Tag color="gold" style={{ marginTop: 4 }}>
                            {formatCurrency(record.topDriver.revenue)}
                        </Tag>
                    </div>
                ) : '-'
            )
        },
        {
            title: 'Calcule le',
            dataIndex: 'calculatedAt',
            key: 'calculatedAt',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        const url = selectedCity
                            ? `/dashboard/rankings/${record.period}?city=${selectedCity}`
                            : `/dashboard/rankings/${record.period}`;
                        navigate(url);
                    }}
                >
                    Voir
                </Button>
            )
        }
    ];

    return (
        <div className="rankings-page">
            {/* Breadcrumb */}
            <Breadcrumb
                separator={<RightOutlined />}
                items={[
                    { title: <Link to="/dashboard">Tableau de bord</Link> },
                    { title: 'Classement Chauffeurs' }
                ]}
                style={{ marginBottom: 24 }}
            />

            {/* Filtre par ville */}
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <EnvironmentOutlined style={{ fontSize: 20, color: '#D4A853' }} />
                    <span style={{ fontWeight: 600 }}>Filtrer par ville :</span>
                    <Select
                        placeholder="Toutes les villes"
                        style={{ width: 250 }}
                        allowClear
                        value={selectedCity}
                        onChange={(value) => setSelectedCity(value)}
                        loading={cities.length === 0}
                    >
                        {cities.map(city => (
                            <Select.Option key={city._id} value={city._id}>
                                {city.name} {city.isDefault && '(Par défaut)'}
                            </Select.Option>
                        ))}
                    </Select>
                    {selectedCity && (
                        <Tag color="blue">
                            {cities.find(c => c._id === selectedCity)?.name || 'Ville sélectionnée'}
                        </Tag>
                    )}
                </div>
            </Card>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Periode courante"
                            value={stats?.currentPeriod ? formatPeriod(stats.currentPeriod) : '-'}
                            prefix={<CalendarOutlined style={{ color: '#D4A853' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Chauffeurs classes"
                            value={stats?.currentStats?.driversCount || 0}
                            prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Periodes"
                            value={stats?.periodsCount || 0}
                            prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Top 3 Current */}
            {stats?.top3 && stats.top3.length > 0 && (
                <Card
                    title={
                        <span>
                            <TrophyOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                            Top 3 du mois
                        </span>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={16}>
                        {stats.top3.map((driver, index) => (
                            <Col xs={24} sm={8} key={index}>
                                <Card
                                    size="small"
                                    style={{
                                        background: index === 0 ? 'linear-gradient(135deg, #fff9e6 0%, #fff 100%)' : undefined,
                                        borderColor: index === 0 ? '#D4A853' : undefined
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div
                                            style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: 18
                                            }}
                                        >
                                            {driver.rank}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600 }}>{driver.firstName} {driver.lastName}</div>
                                            <div style={{ color: '#999', fontSize: 12 }}>{driver.phone}</div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ color: '#D4A853', fontWeight: 700 }}>
                                                {formatCurrency(driver.revenue)}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {/* Actions & Table */}
            <Card
                title={
                    <span>
                        <TrophyOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                        Historique des Classements
                    </span>
                }
                extra={
                    <Can perm="rankings:update">
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Select
                                placeholder="Periode a recalculer"
                                style={{ width: 200 }}
                                allowClear
                                value={selectedPeriod}
                                onChange={setSelectedPeriod}
                            >
                                {periods.map(p => (
                                    <Select.Option key={p.period} value={p.period}>
                                        {formatPeriod(p.period)}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined spin={recalculating} />}
                                onClick={() => handleRecalculate(false)}
                                loading={recalculating}
                            >
                                Recalculer (rapide)
                            </Button>
                            <Button
                                icon={<ReloadOutlined spin={recalculating} />}
                                onClick={() => handleRecalculate(true)}
                                loading={recalculating}
                            >
                                Scan complet
                            </Button>
                        </div>
                    </Can>
                }
            >
                <Table
                    dataSource={periods}
                    columns={columns}
                    rowKey="period"
                    loading={isLoading}
                    pagination={false}
                />
            </Card>
        </div>
    );
};

export default Rankings;
